import * as React from 'react'
import styled from 'styled-components'
import { ChangeEvent, useCallback, useState } from 'react'
import { debounce } from 'debounce'
import { useEvent, useStore } from 'effector-react/ssr'
import { not } from 'ramda'
import { Layout } from '@ui/layout'
import { Social } from '@ui/social'
import Mail from '@assets/icons/mail.svg'
import Phone from '@assets/icons/phone.svg'
import { Devices } from '@styles'
import contactsMobile from '@assets/images/contacts-mobile.jpg'
import contactsDesktop from '@assets/images/contacts-1.jpg'
import { matchMedia, MediaSource } from '@lib/image-media'
import { isClientSide } from '@lib/ssr'
import { useBackgroundOnKroll } from '@lib/use-on-krol'
import * as model from '@pages/contacts/model'

const contactsMedia: MediaSource<string>[] = [
  { mediaQuery: Devices.laptop, source: contactsDesktop },
  { mediaQuery: Devices.mobileS, source: contactsMobile },
]

export const ContactsPage = () => {
  const [email, setEmail] = useState('')
  const [checked, setChecked] = useState(false)

  const toggleCheckbox = () => setChecked(not)

  const contacts = useStore(model.$contacts)
  const subscribed = useStore(model.$subscribed)
  const subscribeIsPending = useStore(model.$subscribeIsPending)

  const subscribe = useEvent(model.subscribe)
  const resetSubscribed = useEvent(model.resetSubscribed)

  const [contactsImage, setContactsImage] = useState('')
  const showBackground = useBackgroundOnKroll()

  const imageUrl = React.useMemo(() => `url(${contactsImage})`, [contactsImage])

  const emailChanged = useCallback((e: ChangeEvent<HTMLInputElement>) => setEmail(e.target.value), [])

  const subscribeClicked = useCallback(() => subscribe({ email }), [email])

  const onResize = React.useCallback(
    debounce(() => setContactsImage(matchMedia(contactsMedia, '')), 100),
    [],
  )

  React.useEffect(() => {
    if (subscribed) {
      setTimeout(() => {
        setEmail('')
        setChecked(false)
        resetSubscribed()
      }, 2000)
    }
  }, [subscribed])

  React.useEffect(() => {
    if (isClientSide()) {
      setContactsImage(matchMedia(contactsMedia, ''))
      window.addEventListener('resize', onResize)
    }

    return () => {
      if (isClientSide()) {
        window.removeEventListener('resize', onResize)
      }
    }
  }, [])

  return (
    <Layout showBorder={showBackground}>
      <ContactsBackground style={{ backgroundImage: imageUrl }} />

      <Contacts>
        <div>
          <Title>Контакты</Title>
          <TextHeader>Сотрудничество</TextHeader>
          <BlockInfo style={{ marginTop: '40px' }}>
            <Phone />
            <Number>{contacts?.phone}</Number>
          </BlockInfo>
          <BlockInfo>
            <Mail />
            <Naming style={{ marginLeft: '20px' }}>{contacts?.email}</Naming>
          </BlockInfo>
        </div>

        <ForPress>
          <TextHeader>Для прессы</TextHeader>
          <DownloadButton href={contacts?.file.url} target='_blank'>
            Скачать пресс-релиз
          </DownloadButton>
        </ForPress>

        {subscribed && <p style={{ margin: '0', marginTop: '50px' }}>Вы успешно подписались на новости!</p>}

        {!subscribed && (
          <>
            <p style={{ margin: '0', marginTop: '50px' }}>Будь в курсе свежих новостей и концертов!</p>

            <div>
              <FollowNews>
                <StyledInput value={email} disabled={subscribeIsPending} onChange={emailChanged} placeholder='E-mail' />
                <FollowButton disabled={!email || !checked || subscribeIsPending} onClick={subscribeClicked}>
                  Подписаться
                </FollowButton>
              </FollowNews>
            </div>

            <Label>
              <Input disabled={subscribeIsPending} checked={checked} onChange={toggleCheckbox} />
              <ConsentText>
                <span> Я согласен(на) на обработку </span>
                <span style={{ borderBottom: '1px solid #333333' }}> персональных данных</span>
              </ConsentText>
            </Label>
          </>
        )}
      </Contacts>

      <SocialWrapper>
        <Social />
      </SocialWrapper>
    </Layout>
  )
}

const Title = styled.h1`
  font-family: 'Magnolia Script', 'SansSerif';
  font-size: 30px;
  line-height: 42px;
  margin: 0;

  margin-bottom: 38px;

  @media screen and ${Devices.laptop} {
    font-size: 80px;
    line-height: 113px;

    margin-bottom: 60px;
  }
`

const SocialWrapper = styled.div`
  display: none;

  @media screen and ${Devices.laptop} {
    display: block;
  }
`

const ContactsBackground = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  z-index: -1;

  width: 100vw;
  height: 100vh;

  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
`

const Contacts = styled.div`
  display: grid;
  grid-template-columns: 469px;
  grid-template-rows: auto;
  width: max-content;
  height: max-content;
  opacity: 0.8;
  padding: 30px 25px;

  @media screen and ${Devices.laptop} {
    margin-left: auto;
    margin-right: 100px;
  }

  @media screen and ${Devices.desktopS} {
    margin-left: auto;
    margin-right: 200px;
  }
`

const TextHeader = styled.span`
  font-family: 'Magnolia Script', 'SansSerif';
  font-style: normal;
  font-weight: normal;
  font-size: 20px;
  line-height: 21px;

  @media screen and ${Devices.laptop} {
    font-size: 30px;
    line-height: 42px;
  }
`

const BlockInfo = styled.div`
  display: flex;
  align-items: center;
  margin-top: 30px;

  &:last-child {
    margin-bottom: 70px;
  }
`

const Naming = styled.div`
  display: flex;
  font-family: Raleway;
  font-style: normal;
  font-weight: normal;
  font-size: 18px;
  line-height: 21px;
`

const Number = styled.div`
  margin-left: 20px;
  font-family: Roboto;
  font-style: normal;
  font-weight: normal;
  font-size: 18px;
  line-height: 21px;
`

const ForPress = styled.div`
  display: flex;
  flex-direction: column;
`

const DownloadButton = styled.a`
  display: flex;
  align-items: center;
  text-decoration: none;
  box-sizing: border-box;
  border: 1px solid #e469d1;
  color: #e469d1;
  background-color: transparent;
  border-radius: 68px;
  padding: 10px 35px;
  margin-top: 15px;
  font-family: Raleway;
  font-size: 16px;
  line-height: 19px;
  width: max-content;

  &:hover {
    background: #e469d1;
    color: #ffffff;
  }
`
const Input = styled.input.attrs({ type: 'checkbox' })`
  appearance: none;
  margin: 0;
  cursor: pointer;
  padding: 0.2em;
  &:before {
    content: '';
    border: 0.2em solid #e469d1;
    border-radius: 0.2em;
    display: inline-block;
    width: 0.9em;
    height: 0.8em;
    padding-left: 0.2em;
    padding-bottom: 0.3em;
    margin-right: 0.2em;
    vertical-align: bottom;
    color: transparent;
    transition: 0.2s;
    background-repeat: no-repeat;
    background-position: center center;
    background-size: 50% 50%;
  }
  &:active:before {
    transform: scale(0);
  }
  &:checked:before {
    background-color: #e469d1;
    border-color: #e469d1;
    color: #fff;
    background-image: url("data:image/svg+xml,%3Csvg width='14' height='10' viewBox='0 0 14 10' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M12.719 0.000476322C12.4196 0.00920293 12.1353 0.134115 11.9263 0.348766C11.6662 0.608874 9.94468 2.37846 8.1672 4.20398C6.72406 5.68612 5.70717 6.72881 5.13468 7.31456L1.90999 4.85852C1.79094 4.75493 1.6519 4.67687 1.50146 4.62919C1.35103 4.58151 1.19241 4.56523 1.03542 4.58133C0.878434 4.59743 0.726424 4.64558 0.588801 4.72281C0.451179 4.80004 0.330873 4.90468 0.235332 5.03029C0.139792 5.15589 0.0710499 5.29977 0.0333631 5.45302C-0.00432371 5.60626 -0.0101533 5.76561 0.016235 5.9212C0.0426234 6.07678 0.100668 6.2253 0.186775 6.35754C0.272882 6.48979 0.38522 6.60296 0.516831 6.69003L4.55219 9.76462C4.77413 9.93347 5.04974 10.0162 5.32797 9.99738C5.6062 9.97861 5.8682 9.85964 6.06545 9.66251C6.32555 9.40242 8.0471 7.63882 9.82458 5.8133C11.6021 3.98779 13.4355 2.10035 13.5597 1.97613C13.7252 1.81378 13.8378 1.60532 13.8831 1.37796C13.9283 1.15059 13.9039 0.914888 13.8131 0.701584C13.7224 0.488279 13.5694 0.30729 13.3742 0.182221C13.1791 0.0571532 12.9507 -0.00618497 12.719 0.000476322Z' fill='white'/%3E%3C/svg%3E%0A");
  }

  &:disabled:before {
    transform: scale(1);
    background-color: #bfb;
    border-color: #bfb;
  }
`

const FollowButton = styled.button`
  display: flex;
  align-items: center;
  text-decoration: none;
  box-sizing: border-box;
  border-radius: 68px;
  padding: 10px 35px;
  margin-top: 15px;
  font-family: Raleway;
  font-size: 16px;
  line-height: 19px;
  width: max-content;
  cursor: pointer;

  border: 1px solid #cb60ba;
  color: #ffffff;
  background-color: #e469d1;

  &:hover {
    background: #cb60ba;
  }

  &:disabled {
    opacity: 25%;
    cursor: not-allowed;
  }
`

const StyledInput = styled.input`
  padding: 10px 35px;
  border-radius: 68px;
  border: 0px solid white;
  font-size: 16px;
  line-height: 19px;
  padding: 10px 35px;
  max-width: max-content;
  margin-right: 30px;
  margin-top: 15px;
`
const FollowNews = styled.div`
  display: flex;
  flex-direction: row;
  margin-bottom: 15px;
`

const Label = styled.label`
  align-items: center;
  display: flex;
`

const ConsentText = styled.p`
  margin: 0px 0px 0px 15px;
  font-size: 12px;
`
